import * as React from 'react';
import { createRendererWithUniDriver } from '../../test/utils';
import { floatingDropdownDriverFactory } from './FloatingDropdown.driver';
import { getFloatingDropdownTestProps } from './test-props';
import { FloatingDropdown } from './FloatingDropdown';
import { TPAComponentsProvider } from '../TPAComponentsConfig';
import { KeyDefinitionType } from '@wix/unidriver-core';
import { createOptionId } from '../Dropdown/helpers';

describe('FloatingDropdown', () => {
  const createDriver = createRendererWithUniDriver(
    floatingDropdownDriverFactory,
  );
  const testProps = getFloatingDropdownTestProps();

  describe('desktop', () => {
    it('should expand dropdown after click on enabled dropdownBase', async () => {
      const { driver } = createDriver(<FloatingDropdown {...testProps} />);
      await driver.clickOnDropdownBase();
      expect(await driver.dropdownContentDisplayed()).toBe(true);
    });

    it('should change isExpanded prop after click on dropdownBase', async () => {
      const { driver } = createDriver(<FloatingDropdown {...testProps} />);
      await driver.clickOnDropdownBase();
      const isAriaExpanded = (await driver.getAriaExpanded()) === 'true';
      expect(isAriaExpanded).toBe(true);
    });

    it('should properly display selected value', async () => {
      const { value, id } = testProps.options[0];
      const { driver } = createDriver(
        <FloatingDropdown {...testProps} value={id} />,
      );
      expect(await driver.getBaseSelectedValue()).toBe(value);
    });

    it('should not expand dropdown after click on disabled dropdownBase', async () => {
      const { driver } = createDriver(
        <FloatingDropdown {...testProps} disabled />,
      );
      await driver.clickOnDropdownBase();
      expect(await driver.dropdownContentDisplayed()).toBe(false);
    });

    describe('unavailable', () => {
      it('should be selectable', async () => {
        const options = [
          {
            id: `0`,
            value: `value-0`,
            isSelectable: true,
          },
          {
            id: `1`,
            value: `value-1`,
            unavailable: true,
          },
        ];
        const onChange = jest.fn();
        const { driver } = createDriver(
          <FloatingDropdown
            {...testProps}
            options={options}
            onChange={onChange}
          />,
        );
        await driver.clickOnDropdownBase();

        await driver.selectOptionAt(1);
        expect(onChange).toHaveBeenCalledWith(options[1]);
      });
    });
  });

  describe('native mobile', () => {
    const FloatingDropdownTest = (props) => {
      return (
        <TPAComponentsProvider value={{ mobile: true }}>
          <FloatingDropdown {...props} />
        </TPAComponentsProvider>
      );
    };

    it('should properly display selected value', async () => {
      const { value, id } = testProps.options[0];
      const { driver } = createDriver(
        <FloatingDropdownTest {...testProps} value={id} />,
      );
      expect(await driver.getBaseSelectedValue()).toBe(value);
    });

    it('should properly display aria-label', async () => {
      const { 'aria-label': ariaLabel } = testProps;
      const { driver } = createDriver(<FloatingDropdownTest {...testProps} />);
      expect(await driver.getAriaLabel()).toBe(ariaLabel);
    });
  });

  describe('a11y', () => {
    describe('keyboard interactions', () => {
      describe('menu trigger', () => {
        it.each(['ArrowDown', 'Enter', 'Space'])(
          'should open dropdown focusing on first option on %s',
          async (keydown: KeyDefinitionType) => {
            const { driver } = createDriver(
              <FloatingDropdown {...testProps} />,
            );

            await driver.keydown(keydown);
            expect(await driver.getAriaActivedescendant()).toBe(
              createOptionId(
                testProps.optionsContainerId,
                testProps.options[0].id,
              ),
            );
          },
        );

        it(`should open dropdown focusing on last option on ArrowUp`, async () => {
          const { driver } = createDriver(<FloatingDropdown {...testProps} />);
          await driver.keydown('ArrowUp');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(
              testProps.optionsContainerId,
              testProps.options[testProps.options.length - 1].id,
            ),
          );
        });
      });

      describe('menu popup', () => {
        it.each(['Space', 'Enter'])(
          'should call onChange with focused option on %s',
          async (keydown: KeyDefinitionType) => {
            const onChange = jest.fn();
            const { driver } = createDriver(
              <FloatingDropdown {...testProps} onChange={onChange} />,
            );
            await driver.keydown('ArrowDown');
            await driver.keydown('ArrowDown');
            await driver.keydown('ArrowDown');
            await driver.keydown('ArrowDown');
            await driver.keydown(keydown);

            expect(onChange).toBeCalledWith(testProps.options[4]);
          },
        );

        it.each(['Space', 'Enter'])(
          'should not call onChange on disabled option on %s',
          async (keydown: KeyDefinitionType) => {
            const onChange = jest.fn();
            const { driver } = createDriver(
              <FloatingDropdown {...testProps} onChange={onChange} />,
            );
            await driver.keydown('Enter');
            await driver.keydown('ArrowDown');
            await driver.keydown(keydown);
            expect(onChange).toBeCalledTimes(0);
            await driver.keydown('ArrowUp');
            await driver.keydown(keydown);
            expect(onChange).toBeCalledWith(testProps.options[0]);
          },
        );
        it('should close on Escape', async () => {
          const { driver } = createDriver(<FloatingDropdown {...testProps} />);
          await driver.keydown('Enter');
          expect(await driver.dropdownContentDisplayed()).toBe(true);
          await driver.keydown('Escape');
          expect(await driver.dropdownContentDisplayed()).toBe(false);
        });

        it('should move focus on ArrowDown', async () => {
          const { driver } = createDriver(<FloatingDropdown {...testProps} />);
          await driver.keydown('Enter');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');

          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(
              testProps.optionsContainerId,
              testProps.options[2].id,
            ),
          );
        });
        it('should focus first option on ArrowDown if it is last option', async () => {
          const { driver } = createDriver(<FloatingDropdown {...testProps} />);
          await driver.keydown('Enter');
          await driver.keydown('End');
          await driver.keydown('ArrowDown');

          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(
              testProps.optionsContainerId,
              testProps.options[0].id,
            ),
          );
        });

        it('should move focus on ArrowUp', async () => {
          const { driver } = createDriver(<FloatingDropdown {...testProps} />);
          await driver.keydown('Enter');
          await driver.keydown('End');
          await driver.keydown('ArrowUp');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(
              testProps.optionsContainerId,
              testProps.options[testProps.options.length - 2].id,
            ),
          );
        });
        it('should focus last option on ArrowUp if it is first option', async () => {
          const { driver } = createDriver(<FloatingDropdown {...testProps} />);
          await driver.keydown('Enter');
          await driver.keydown('ArrowUp');

          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(
              testProps.optionsContainerId,
              testProps.options[testProps.options.length - 1].id,
            ),
          );
        });

        it('should move focus to first option on Home', async () => {
          const { driver } = createDriver(<FloatingDropdown {...testProps} />);
          await driver.keydown('Enter');
          await driver.keydown('End');
          await driver.keydown('Home');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(
              testProps.optionsContainerId,
              testProps.options[0].id,
            ),
          );
        });

        it('should move focus to last option on End', async () => {
          const { driver } = createDriver(<FloatingDropdown {...testProps} />);
          await driver.keydown('Enter');
          await driver.keydown('End');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(
              testProps.optionsContainerId,
              testProps.options[testProps.options.length - 1].id,
            ),
          );
        });
      });

      describe('letters', () => {
        const optionsLettered = [
          { id: '0', value: 'apple', isSelectable: true },
          { id: '1', value: 'banana', isSelectable: true },
          {
            id: '2',
            value: 'blueberry',
            isSelectable: true,
            unavailable: true,
          },
          { id: '3', value: 'boysenberry', isSelectable: true },
          { id: '4', value: 'cherry', isSelectable: true },
          { id: '5', value: 'durian', isSelectable: false },
          { divider: true },
          { id: '6', value: 'Eggfruit', isSelectable: true },
          { id: '7', value: 'eggplant', isSelectable: true },
          { id: '8', value: 'elderberry', isSelectable: false },
          { id: '9', value: 'grape', isSelectable: true, isDisabled: true },
          { id: '10', value: 'honeydew', isSelectable: true },
          { id: '11', value: 'italian plum', isSelectable: true },
          { id: '12', value: 'indian fig', isSelectable: true },
        ];

        it('should find first match on letter keydown', async () => {
          const { driver } = createDriver(
            <FloatingDropdown {...testProps} options={optionsLettered} />,
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('e');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(testProps.optionsContainerId, optionsLettered[7].id),
          );
        });

        it('should focus on next match on letter keydown', async () => {
          const { driver } = createDriver(
            <FloatingDropdown {...testProps} options={optionsLettered} />,
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('e');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(testProps.optionsContainerId, optionsLettered[7].id),
          );
          await driver.keydown('e');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(testProps.optionsContainerId, optionsLettered[8].id),
          );
        });

        it('should iterate to the first match after the last match had focus', async () => {
          const { driver } = createDriver(
            <FloatingDropdown {...testProps} options={optionsLettered} />,
          );
          await driver.keydown('b');
          await driver.keydown('b');
          await driver.keydown('b');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(testProps.optionsContainerId, optionsLettered[3].id),
          );
          await driver.keydown('b');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(testProps.optionsContainerId, optionsLettered[1].id),
          );
        });

        it('should not change focus if match has not been found', async () => {
          const { driver } = createDriver(
            <FloatingDropdown {...testProps} options={optionsLettered} />,
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('b');
          await driver.keydown('b');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(testProps.optionsContainerId, optionsLettered[2].id),
          );
          await driver.keydown('z');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(testProps.optionsContainerId, optionsLettered[2].id),
          );
        });

        it('should go to the first match if multiple letters are quickly typed and focused option starts with the same letter', async () => {
          // current focus - boysenberry. if "ba" is quickly typed it should go to banana.
          const { driver } = createDriver(
            <FloatingDropdown {...testProps} options={optionsLettered} />,
          );
          await driver.keydown('b');
          await driver.keydown('ArrowDown');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(testProps.optionsContainerId, optionsLettered[2].id),
          );
          await driver.keydown('b');
          await driver.keydown('a');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(testProps.optionsContainerId, optionsLettered[1].id),
          );
        });

        it('should not break if option id contains special characters', async () => {
          const specialChars = [' ', ':', '.', '+'];
          const optionsWithSpecialChars = optionsLettered.map((opt, index) =>
            opt.id ? { ...opt, id: opt.id + specialChars[index % 4] } : opt,
          );
          const { driver } = createDriver(
            <FloatingDropdown
              {...testProps}
              options={optionsWithSpecialChars}
            />,
          );
          await driver.keydown('h');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(
              testProps.optionsContainerId,
              optionsWithSpecialChars[11].id,
            ),
          );
        });
      });
    });
  });
});
